import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Bug, Loader2, Sparkles, Trash2, CheckCircle2, AlertTriangle, ShieldAlert, Wrench, Flame, TrendingUp, Share2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/Bits";
import { toast } from "sonner";

const CATEGORIES = [
  { key: "balance", label: "Balance" },
  { key: "pacing", label: "Pacing" },
  { key: "controls", label: "Controls" },
  { key: "content", label: "Content" },
  { key: "fun", label: "Fun factor" },
];

const scoreColor = (v) => (v >= 75 ? "#22c55e" : v >= 50 ? "#eab308" : "#f43f5e");
const SEVERITY = {
  high: { label: "High", cls: "bg-rose-500/15 text-rose-400 border-rose-500/40", Icon: ShieldAlert },
  medium: { label: "Medium", cls: "bg-amber-500/15 text-amber-400 border-amber-500/40", Icon: AlertTriangle },
  low: { label: "Low", cls: "bg-sky-500/15 text-sky-400 border-sky-500/40", Icon: AlertTriangle },
};

export default function QAStudio() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [reports, setReports] = useState([]);
  const [running, setRunning] = useState(false);
  const [applying, setApplying] = useState(null);
  const [autofixing, setAutofixing] = useState(false);
  const [retestFrom, setRetestFrom] = useState(null);
  const [delta, setDelta] = useState(null);
  const prevStatusRef = useRef();
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/qa/${gameId}`);
      setReport(data?.latest || null);
      setReports(data?.reports || []);
    } catch (e) { toast.error("Couldn't open the AI Playtester."); }
    finally { setLoading(false); }
  }, [gameId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (report?.status === "processing") {
      pollRef.current = setInterval(load, 4000);
      return () => clearInterval(pollRef.current);
    }
  }, [report?.status, load]);

  // Auto-retest delta: when a re-test finishes, show the score change since the fix.
  useEffect(() => {
    if (prevStatusRef.current === "processing" && report?.status === "ready" && retestFrom != null) {
      setDelta((report.overall || 0) - retestFrom);
      setRetestFrom(null);
    }
    prevStatusRef.current = report?.status;
  }, [report?.status, report?.overall, retestFrom]);

  const run = async () => {
    setRunning(true);
    try {
      const { data } = await api.post(`/qa/${gameId}/run`, {}, { timeout: 40000 });
      setReport(data);
      toast.success("🧪 Playtesting your game — the report lands in ~20 seconds.");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((typeof d === "string" ? d : formatApiError(d)) || "Couldn't start the playtest.");
    } finally { setRunning(false); }
  };

  const retest = () => {
    setDelta(null);
    setRetestFrom(report?.overall ?? 0);
    run();
  };

  const shareStreak = async (text) => {
    try {
      if (navigator.share) await navigator.share({ title: "GameView Studio", text });
      else { await navigator.clipboard.writeText(text); toast.success("Badge copied — paste it anywhere! 🎉"); }
    } catch (e) { /* user dismissed */ }
  };

  const remove = async () => {
    if (!report) return;
    const id = report.id;
    setReport(null);
    try { await api.delete(`/qa/item/${id}`); } catch { /* optimistic */ }
  };

  const applyFix = async (i) => {
    setApplying(i);
    try {
      await api.post(`/qa/${gameId}/apply`, { index: i });
      setReport((r) => {
        const issues = [...(r.issues || [])];
        issues[i] = { ...issues[i], applied: true };
        return { ...r, issues };
      });
      toast.success("✨ Fix applied — re-test now to see your new score.");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((typeof d === "string" ? d : formatApiError(d)) || "Couldn't apply that fix.");
    } finally { setApplying(null); }
  };

  const autofixAll = async () => {
    setAutofixing(true);
    try {
      const { data } = await api.post(`/qa/${gameId}/autofix`, {}, { timeout: 40000 });
      if (data.status === "done") {
        setAutofixing(false);
        if ((data.applied || 0) > 0) { await load(); toast.success(`🤖 Auto-fixed ${data.applied} issue${data.applied === 1 ? "" : "s"} — re-test to see your new score.`); }
        else toast.message("Nothing left to auto-fix — you're all patched up!");
        return;
      }
      toast.message(`🤖 Robot is fixing ${data.todo || ""} issue${data.todo === 1 ? "" : "s"}… this takes a moment.`);
      // Poll until the background auto-fix finishes.
      let tries = 0;
      const poll = setInterval(async () => {
        tries += 1;
        try {
          const { data: r } = await api.get(`/qa/${gameId}`);
          const l = r?.latest;
          if (l?.autofix_status === "done" || tries > 40) {
            clearInterval(poll);
            setAutofixing(false);
            setReport(l || null);
            setReports(r?.reports || []);
            const n = l?.autofix_applied || 0;
            if (n > 0) toast.success(`🤖 Auto-fixed ${n} issue${n === 1 ? "" : "s"} — re-test to see your new score.`);
            else if (tries > 40) toast.message("Auto-fix is taking a while — check back shortly.");
            else toast.message("No changes were needed.");
          }
        } catch { /* keep polling */ }
      }, 4000);
    } catch (e) {
      setAutofixing(false);
      const d = e.response?.data?.detail;
      toast.error((typeof d === "string" ? d : formatApiError(d)) || "Couldn't auto-fix — please try again.");
    }
  };

  if (loading) return <div className="min-h-[60vh] grid place-items-center"><Spinner /></div>;

  const processing = report?.status === "processing";
  const failed = report?.status === "failed";
  const ready = report?.status === "ready";

  return (
    <div className="max-w-4xl mx-auto px-4 py-6" data-testid="qa-studio">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/app/build/${gameId}`)} data-testid="qa-back-btn"><ArrowLeft className="w-5 h-5" /></Button>
          <div className="min-w-0">
            <h1 className="font-display text-2xl font-bold flex items-center gap-2 truncate"><Bug className="w-6 h-6 text-violet-500" /> AI Playtester</h1>
            <p className="text-xs text-[hsl(var(--muted-foreground))] truncate">A bot playtests your game and scores balance, pacing, controls, content & fun</p>
          </div>
        </div>
        {ready && <Button variant="ghost" size="sm" className="rounded-full text-[hsl(var(--muted-foreground))]" onClick={remove} data-testid="qa-delete-btn"><Trash2 className="w-4 h-4" /> Discard</Button>}
      </div>

      {/* Runner */}
      <div className="rounded-2xl border border-violet-500/40 bg-gradient-to-br from-violet-500/10 to-fuchsia-500/5 p-4 mb-6">
        <p className="text-[11px] font-bold uppercase tracking-wide text-violet-600 dark:text-violet-400 mb-2 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> {report ? "Run a fresh playtest" : "Playtest your game"}</p>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">An AI QA lead reviews your game's design and returns an honest, constructive report — a score out of 100 plus the top things to fix.</p>
        <Button className="rounded-full" onClick={run} disabled={running || processing} data-testid="qa-run-btn">
          {running || processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bug className="w-4 h-4" />} {running || processing ? "Playtesting…" : (report ? "Run again" : "Run playtest")}
        </Button>
        <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-2">Uses 15 AI credits · AI-generated feedback — use your judgement.</p>
      </div>

      {!report && (
        <div className="rounded-2xl border border-dashed border-[hsl(var(--border))] p-10 text-center" data-testid="qa-empty">
          <Bug className="w-10 h-10 mx-auto text-[hsl(var(--muted-foreground))] mb-3" />
          <p className="text-sm text-[hsl(var(--muted-foreground))]">No playtest yet — hit <b>Run playtest</b> to get an AI QA report on your game.</p>
        </div>
      )}

      {processing && (
        <div className="rounded-2xl border border-[hsl(var(--border))] p-10 text-center" data-testid="qa-processing">
          <Loader2 className="w-9 h-9 mx-auto text-violet-500 animate-spin mb-3" />
          <p className="text-sm font-semibold">The bot is playing your game…</p>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">This takes about 20 seconds — the report appears automatically.</p>
        </div>
      )}

      {failed && (
        <div className="rounded-2xl border border-rose-500/40 bg-rose-500/5 p-8 text-center" data-testid="qa-failed">
          <p className="text-sm text-rose-500 font-semibold">The playtest failed — please try again.</p>
        </div>
      )}

      {ready && (
        <div className="space-y-6" data-testid="qa-report">
          {/* Improvement streak ribbon + milestone badge */}
          {(() => {
            const readyReports = (reports || []).filter((r) => r.status === "ready");
            const totalFixes = (reports || []).reduce((n, r) => n + (r.issues || []).filter((i) => i.applied).length, 0);
            const gain = readyReports.length >= 2 ? (readyReports[0].overall || 0) - (readyReports[readyReports.length - 1].overall || 0) : null;
            if (totalFixes <= 0) return null;
            const milestone = totalFixes >= 25 ? "QA Legend" : totalFixes >= 10 ? "QA Pro" : totalFixes >= 5 ? "On a roll" : (gain != null && gain >= 25 ? "Big glow-up" : null);
            const shareText = `${milestone ? milestone + "! " : ""}I've applied ${totalFixes} AI playtest ${totalFixes === 1 ? "fix" : "fixes"}${gain != null && gain > 0 ? ` and lifted "${report.game_title || "my game"}" by +${gain} points` : ""} on GameView Studio 🎮`;
            return (
              <div className={`rounded-2xl border px-4 py-3 ${milestone ? "border-amber-500/60 bg-gradient-to-r from-amber-500/20 via-orange-500/15 to-fuchsia-500/10 shadow-[0_0_0_1px_rgba(245,158,11,0.15)]" : "border-amber-500/40 bg-gradient-to-r from-amber-500/15 to-orange-500/10"}`} data-testid="qa-streak-ribbon">
                <div className="flex items-center gap-3">
                  <div className="grid place-items-center w-9 h-9 rounded-full bg-amber-500/20 shrink-0">
                    <Flame className="w-4.5 h-4.5 text-amber-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold leading-tight flex items-center gap-2 flex-wrap">
                      {milestone && <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/20 border border-amber-500/50 px-2 py-0.5 text-[11px] uppercase tracking-wide text-amber-500" data-testid="qa-milestone-badge">🏆 {milestone}</span>}
                      <span>{totalFixes} {totalFixes === 1 ? "fix" : "fixes"} applied
                        {gain != null && (
                          <span className={gain >= 0 ? "text-emerald-500" : "text-rose-500"}> → {gain >= 0 ? "+" : ""}{gain} score</span>
                        )}
                      </span>
                    </p>
                    <p className="text-[11px] text-[hsl(var(--muted-foreground))]">
                      {gain == null ? "Re-test to measure how much your score improved." :
                        gain > 0 ? "Your improvement streak is paying off — keep polishing!" :
                          gain === 0 ? "Score held steady across your fixes." : "Some fixes lowered the score — try re-testing after each change."}
                    </p>
                  </div>
                  {gain != null && gain > 0 && !milestone && (
                    <div className="hidden sm:flex items-center gap-1 text-emerald-500 font-bold text-sm shrink-0" data-testid="qa-streak-gain">
                      <TrendingUp className="w-4 h-4" /> +{gain}
                    </div>
                  )}
                </div>
                {milestone && (
                  <Button size="sm" variant="outline" className="rounded-full mt-3 border-amber-500/50 hover:bg-amber-500/10" onClick={() => shareStreak(shareText)} data-testid="qa-streak-share">
                    <Share2 className="w-3.5 h-3.5" /> Share your badge
                  </Button>
                )}
              </div>
            );
          })()}

          {/* Re-test nudge after applying a fix */}
          {(report.issues || []).some((it) => it.applied) && (
            <div className="rounded-2xl border border-violet-500/40 bg-violet-500/10 p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="qa-retest-banner">
              <Wrench className="w-5 h-5 text-violet-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold">You've applied a fix to this game</p>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">Run a fresh playtest to see how much your score improved.</p>
              </div>
              <Button size="sm" className="rounded-full shrink-0" onClick={retest} disabled={running || processing} data-testid="qa-retest-btn">
                {running || processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bug className="w-4 h-4" />} Re-test now
              </Button>
            </div>
          )}

          {/* Overall */}
          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 flex items-center gap-5">
            <div className="relative w-24 h-24 shrink-0 grid place-items-center rounded-full" style={{ background: `conic-gradient(${scoreColor(report.overall)} ${report.overall * 3.6}deg, hsl(var(--secondary)) 0deg)` }}>
              <div className="w-[76px] h-[76px] rounded-full bg-[hsl(var(--card))] grid place-items-center">
                <span className="text-2xl font-bold tabular-nums" data-testid="qa-overall">{report.overall}</span>
              </div>
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Overall playtest score</p>
              <p className="text-lg font-semibold leading-snug mt-0.5" data-testid="qa-verdict">{report.verdict}</p>
              {delta != null && (
                <span
                  className={`mt-1.5 inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-bold ${delta > 0 ? "bg-emerald-500/15 text-emerald-500 border-emerald-500/40" : delta < 0 ? "bg-rose-500/15 text-rose-500 border-rose-500/40" : "bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))] border-[hsl(var(--border))]"}`}
                  data-testid="qa-delta">
                  {delta > 0 ? `▲ +${delta} since your fix` : delta < 0 ? `▼ ${delta} since your fix` : "→ no change since your fix"}
                </span>
              )}
            </div>
          </div>

          {/* Category scores */}
          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5" data-testid="qa-scores">
            <p className="text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-3">Category scores</p>
            <div className="space-y-2.5">
              {CATEGORIES.map((c) => {
                const v = report.scores?.[c.key] ?? 0;
                return (
                  <div key={c.key} className="flex items-center gap-3" data-testid={`qa-score-${c.key}`}>
                    <span className="text-xs font-semibold w-24 shrink-0">{c.label}</span>
                    <div className="flex-1 h-2 rounded-full bg-[hsl(var(--secondary))] overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${v}%`, background: scoreColor(v) }} />
                    </div>
                    <span className="text-xs font-bold tabular-nums w-8 text-right">{v}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Highlights */}
          {(report.highlights || []).length > 0 && (
            <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/5 p-5" data-testid="qa-highlights">
              <p className="text-[11px] font-bold uppercase tracking-wide text-emerald-600 dark:text-emerald-400 mb-2 flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> What's working</p>
              <ul className="space-y-1.5">
                {report.highlights.map((h, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm" data-testid={`qa-highlight-${i}`}><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /> {h}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Issues */}
          {(report.issues || []).length > 0 && (
            <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5" data-testid="qa-issues">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <p className="text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Top things to fix</p>
                {report.bot_count > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-violet-500/15 border border-violet-500/40 px-2 py-0.5 text-[10px] font-bold text-violet-400" data-testid="qa-bot-count">
                    🤖 {report.bot_count} robot-found
                  </span>
                )}
                {(report.issues || []).some((it) => it.fix && !it.applied) && (
                  <Button size="sm" className="ml-auto h-7 rounded-full text-xs bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white hover:opacity-90" onClick={autofixAll} disabled={autofixing} data-testid="qa-autofix-btn">
                    {autofixing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wrench className="w-3.5 h-3.5" />} Auto-Fix All
                  </Button>
                )}
              </div>
              <div className="space-y-3">
                {report.issues.map((it, i) => {
                  const sev = SEVERITY[it.severity] || SEVERITY.medium;
                  return (
                    <div key={i} className="rounded-xl border border-[hsl(var(--border))] p-3" data-testid={`qa-issue-${i}`}>
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold ${sev.cls}`}><sev.Icon className="w-3 h-3" /> {sev.label}</span>
                        {it.bot && <span className="inline-flex items-center gap-1 rounded-full bg-violet-500/15 border border-violet-500/40 px-2 py-0.5 text-[10px] font-bold text-violet-400" data-testid={`qa-issue-bot-${i}`} title="Found by the robot playtester">🤖 Robot</span>}
                        <span className="text-sm font-semibold">{it.title}</span>
                        {it.applied
                          ? <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-bold text-emerald-500" data-testid={`qa-applied-${i}`}><CheckCircle2 className="w-3.5 h-3.5" /> Applied</span>
                          : <Button size="sm" variant="outline" className="ml-auto h-7 rounded-full text-xs" onClick={() => applyFix(i)} disabled={applying === i || autofixing} data-testid={`qa-apply-${i}`}>{applying === i ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wrench className="w-3.5 h-3.5" />} Apply fix</Button>}
                      </div>
                      {it.fix && <p className="text-xs text-[hsl(var(--muted-foreground))] flex items-start gap-1.5"><Wrench className="w-3.5 h-3.5 shrink-0 mt-0.5 text-violet-500" /> {it.fix}</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {reports.length > 1 && (
        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 mt-6" data-testid="qa-history">
          <p className="text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-3">Score history</p>
          {(() => {
            const pts = reports.slice().reverse();
            const n = pts.length, W = 320, H = 60, pad = 6;
            const x = (i) => pad + (i * (W - 2 * pad)) / Math.max(1, n - 1);
            const y = (v) => H - pad - (v / 100) * (H - 2 * pad);
            const poly = pts.map((r, i) => `${x(i)},${y(r.overall || 0)}`).join(" ");
            const first = pts[0].overall || 0, last = pts[n - 1].overall || 0;
            return (
              <div>
                <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-16">
                  <polyline points={poly} fill="none" stroke="#8b5cf6" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                  {pts.map((r, i) => <circle key={i} cx={x(i)} cy={y(r.overall || 0)} r="3" fill={scoreColor(r.overall || 0)} />)}
                </svg>
                <div className="flex items-center justify-between text-[10px] text-[hsl(var(--muted-foreground))] mt-1">
                  <span data-testid="qa-history-count">{n} playtests</span>
                  <span>latest {last} {last > first ? "↑ improving" : last < first ? "↓ down" : "→ steady"}</span>
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}
